import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './SafeIcon';
import { supabase } from '@/integrations/supabase/client';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiShield, FiEdit, FiRefreshCw, FiAlertCircle, FiCheck, FiX, FiToggleLeft, FiToggleRight, FiCheckCircle, FiSync, FiCreditCard, FiSearch, FiChevronDown, FiZap, FiAward, FiBuilding, FiUser, FiTrash2 } = FiIcons;

const UserManagementPanel = () => {
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [editForm, setEditForm] = useState({ role: '', planId: '', status: '', billingType: 'monthly', isInTrial: false, billingStatus: 'none', aiProvider: '', aiModel: '' });
  const [connectionStatus, setConnectionStatus] = useState('testing');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // user to delete
  const [deleting, setDeleting] = useState(false);

  // Fetch subscription plans. Hides `free` — it's a legacy alias for
  // `starter` kept only so old user_profiles.subscription_plan_id='free'
  // rows stay FK-valid; the admin panel should only expose the 4 real
  // plans (Starter, Pro Trader, Institutional, Enterprise).
  const fetchPlans = async () => {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .neq('id', 'free')
      .order('price_usd_month', { ascending: true, nullsFirst: false });

    if (!error && data) {
      setPlans(data);
    }
  };

  // Human-readable price label — handles $0 (Free), custom pricing
  // (Enterprise), and numeric monthly. Falsy-safe: price 0 no longer
  // falls into the "(Custom)" branch.
  const planPriceLabel = (plan) => {
    if (plan.is_custom_pricing) return '(Contact sales)';
    if (plan.price_usd_month === 0) return '(Free)';
    if (typeof plan.price_usd_month === 'number') return `($${plan.price_usd_month}/mo)`;
    return '';
  };

  // Test database connection
  const testConnection = async () => {
    try {
      setConnectionStatus('testing');
      const { error } = await supabase
        .from('user_profiles')
        .select('count')
        .limit(1);

      setConnectionStatus(error ? 'failed' : 'connected');
      return !error;
    } catch {
      setConnectionStatus('failed');
      return false;
    }
  };

  // Synchronize all users from auth.users to user_profiles
  const syncAllUsers = async () => {
    try {
      setSyncing(true);
      const { data, error } = await supabase.rpc('admin_sync_users');

      if (error || !data?.success) {
        setError(`Sync failed: ${error?.message || data?.error}`);
        return;
      }

      setError(null);
      await fetchUsers();
    } catch (err) {
      setError(`Sync error: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  // Fetch users using the enhanced admin functions
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const connectionOk = await testConnection();
      if (!connectionOk) {
        setError('Database connection failed.');
        return;
      }

      const { data: usersResponse, error: usersError } = await supabase
        .rpc('admin_get_users_with_plans');

      if (usersError) {
        // Fallback to direct query
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('user_profiles')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (!fallbackError && fallbackData) {
          setUsers(fallbackData);
        } else {
          setError(`Error: ${fallbackError?.message}`);
        }
        return;
      }

      if (usersResponse?.success) {
        setUsers(usersResponse.users || []);
      } else {
        setUsers([]);
      }
    } catch (err) {
      setError(`Critical error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      setUpdatingUser(userId);
      const { data, error } = await supabase.rpc('admin_update_role', {
        target_user_id: userId,
        new_role: newRole
      });

      if (error || !data?.success) {
        setError(`Failed to update role: ${error?.message || data?.error}`);
        return;
      }

      setError(`✅ Role updated to ${newRole}!`);
      setTimeout(() => setError(null), 3000);
      await fetchUsers();
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setUpdatingUser(null);
    }
  };

  const updateUserPlan = async (userId, planId, status) => {
    try {
      setUpdatingUser(userId);
      const { data, error } = await supabase.rpc('admin_update_user_plan', {
        p_user_id: userId,
        p_plan_id: planId || null,
        p_status: status || 'active'
      });

      if (error || !data?.success) {
        setError(`Failed to update plan: ${error?.message || data?.error}`);
        return;
      }

      setError(`✅ Subscription updated!`);
      setTimeout(() => setError(null), 3000);
      await fetchUsers();
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setUpdatingUser(null);
    }
  };

  const toggleUserStatus = async (userId) => {
    try {
      setUpdatingUser(userId);
      const { data, error } = await supabase.rpc('admin_toggle_user_active', {
        target_user_id: userId
      });

      if (error || !data?.success) {
        setError(`Failed to toggle status: ${error?.message || data?.error}`);
        return;
      }

      setError(`✅ Status toggled!`);
      setTimeout(() => setError(null), 3000);
      await fetchUsers();
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setUpdatingUser(null);
    }
  };

  const deleteUser = async (userId) => {
    try {
      setDeleting(true);
      const { data, error } = await supabase.rpc('admin_delete_user', {
        p_user_id: userId
      });

      if (error || !data?.success) {
        setError(`Failed to delete user: ${error?.message || data?.error}`);
        return;
      }

      setError(`✅ User ${data.email} deleted successfully!`);
      setTimeout(() => setError(null), 3000);
      await fetchUsers();
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const openEditPanel = (user) => {
    setSelectedUser(user);
    setEditForm({
      role: user.role || 'user',
      planId: user.subscription_plan_id || '',
      status: user.subscription_status || 'active',
      billingType: user.billing_type || 'monthly',
      isInTrial: user.is_in_trial || false,
      billingStatus: user.billing_status || 'none',
      aiProvider: user.ai_provider || '',
      aiModel: user.ai_model || ''
    });
    setSidePanelOpen(true);
  };

  const saveUserChanges = async () => {
    if (!selectedUser) return;
    
    setUpdatingUser(selectedUser.user_id);
    
    try {
      // Update role if changed
      if (editForm.role !== selectedUser.role) {
        await updateUserRole(selectedUser.user_id, editForm.role);
      }
      
      // Update plan and billing fields
      // CRITICAL: If billing_status is 'active', ensure a plan is set
      const effectivePlanId = editForm.planId || (editForm.billingStatus === 'active' ? 'institutional_ai' : null);
      
      const updates = {
        subscription_plan_id: effectivePlanId,
        subscription_status: editForm.status,
        billing_type: editForm.billingType,
        is_in_trial: editForm.isInTrial,
        billing_status: editForm.billingStatus,
        ai_provider: editForm.aiProvider || null,
        ai_model: editForm.aiModel || null,
        updated_at: new Date().toISOString()
      };
      
      // Set paid_until based on billing type
      if (editForm.billingType === 'lifetime') {
        updates.paid_until = '2099-12-31T23:59:59Z';
      } else if (editForm.billingStatus === 'active' && !editForm.isInTrial) {
        // Monthly/annual - set paid_until based on billing type
        const daysToAdd = editForm.billingType === 'annual' ? 365 : 30;
        if (!selectedUser.paid_until || new Date(selectedUser.paid_until) < new Date()) {
          updates.paid_until = new Date(Date.now() + daysToAdd * 24 * 60 * 60 * 1000).toISOString();
        }
      }
      
      // Trial fields: never overwrite a valid future trial_ends_at.
      // The Growth Feedback panel's admin_grant_reply_trial RPC writes a
      // real trial_ends_at (e.g. 3 months out); toggling this switch used
      // to hard-set it back to now()+30d, which silently truncated grants
      // like Samo's 92-day trial down to 30 days. Now:
      //   - Toggling OFF still clears both trial fields (explicit intent).
      //   - Toggling ON preserves an existing valid trial_ends_at; only
      //     defaults to 30 days when nothing valid is on file.
      const now = Date.now();
      const existingEnd = selectedUser.trial_ends_at
        ? new Date(selectedUser.trial_ends_at).getTime()
        : 0;
      if (!editForm.isInTrial) {
        updates.trial_ends_at = null;
        updates.trial_started_at = null;
      } else if (editForm.isInTrial && !selectedUser.is_in_trial) {
        updates.trial_started_at = selectedUser.trial_started_at || new Date().toISOString();
        updates.trial_ends_at = existingEnd > now
          ? selectedUser.trial_ends_at // keep the real grant date
          : new Date(now + 30 * 86_400_000).toISOString(); // fallback default
      }
      
      const { error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('user_id', selectedUser.user_id);
      
      if (error) {
        setError(`Failed to update: ${error.message}`);
      } else {
        setError('✅ User updated successfully!');
        setTimeout(() => setError(null), 3000);
      }
      
      await fetchUsers();
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setUpdatingUser(null);
      setSidePanelOpen(false);
      setSelectedUser(null);
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchUsers();
  }, []);

  const getPlanBadge = (user) => {
    const plan = user.plan;
    if (!plan) {
      return { color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', text: 'No Plan', icon: FiUser };
    }
    
    const tierConfig = {
      retail: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: FiZap },
      professional: { color: 'bg-violet-500/20 text-violet-400 border-violet-500/30', icon: FiAward },
      institutional: { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: FiShield },
      enterprise: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: FiBuilding }
    };
    
    const config = tierConfig[plan.tier] || tierConfig.retail;
    return { ...config, text: plan.name };
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && user.is_active) ||
                         (statusFilter === 'inactive' && !user.is_active);
    
    const userPlanId = user.subscription_plan_id || '';
    const matchesSubscription = subscriptionFilter === 'all' || 
                               (subscriptionFilter === 'none' && !userPlanId) ||
                               userPlanId === subscriptionFilter;
    
    return matchesSearch && matchesRole && matchesStatus && matchesSubscription;
  });

  // Connection status indicator
  const ConnectionIndicator = () => {
    const statusConfig = {
      testing: { icon: FiRefreshCw, color: 'text-purple-400', spin: true, text: 'Testing...' },
      connected: { icon: FiCheckCircle, color: 'text-cyan-400', spin: false, text: 'Connected' },
      failed: { icon: FiAlertCircle, color: 'text-purple-400', spin: false, text: 'Failed' }
    };
    
    const config = statusConfig[connectionStatus];
    
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 backdrop-blur-sm border border-purple-500/30"
      >
        <SafeIcon 
          icon={config.icon} 
          className={`${config.color} ${config.spin ? 'animate-spin' : ''}`} 
        />
        <span className={`text-sm font-medium ${config.color}`}>{config.text}</span>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/50 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50">
        <div className="flex items-center justify-center gap-4 py-12">
          <div className="w-12 h-12 rounded-full border-4 border-slate-700 border-t-blue-500 animate-spin" />
          <span className="text-slate-300 text-lg font-medium">Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-gradient-to-br from-slate-900/80 to-slate-800/50 backdrop-blur-xl rounded-2xl p-6 lg:p-8 border border-slate-700/50 shadow-2xl"
      >
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-purple-500/30">
              <SafeIcon icon={FiUsers} className="text-cyan-400 text-2xl" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white tracking-tight">User Management</h2>
              <p className="text-slate-400 text-sm mt-0.5">{users.length} registered users</p>
            </div>
          </div>
          <ConnectionIndicator />
        </div>

        {/* Toast Notifications */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              className={`${error.startsWith('✅') 
                ? 'bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border-purple-500/30 text-cyan-400' 
                : 'bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border-purple-500/30 text-purple-400'
              } border rounded-xl p-4 mb-6 backdrop-blur-sm`}
            >
              <div className="flex items-center gap-3">
                <SafeIcon icon={error.startsWith('✅') ? FiCheck : FiAlertCircle} className="text-lg" />
                <span className="font-medium flex-1">{error.replace('✅ ', '')}</span>
                <button
                  onClick={() => setError(null)}
                  className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search and Filters */}
        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl pl-12 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
            />
          </div>
          
          <div className="flex gap-3 flex-wrap">
            <div className="relative">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="appearance-none bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 pr-10 text-white focus:outline-none focus:border-blue-500/50 cursor-pointer hover:bg-slate-700/50 transition-all"
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </select>
              <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>

            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="appearance-none bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 pr-10 text-white focus:outline-none focus:border-blue-500/50 cursor-pointer hover:bg-slate-700/50 transition-all"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>

            <div className="relative">
              <select
                value={subscriptionFilter}
                onChange={(e) => setSubscriptionFilter(e.target.value)}
                className="appearance-none bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 pr-10 text-white focus:outline-none focus:border-blue-500/50 cursor-pointer hover:bg-slate-700/50 transition-all"
              >
                <option value="all">All Plans</option>
                <option value="none">No Plan</option>
                {plans.map(plan => (
                  <option key={plan.id} value={plan.id}>{plan.name}</option>
                ))}
              </select>
              <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>

          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={syncAllUsers}
              disabled={syncing}
              className="flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 disabled:opacity-50 text-white font-medium shadow-lg shadow-purple-500/25 transition-all"
            >
              <SafeIcon icon={syncing ? FiRefreshCw : FiSync} className={syncing ? 'animate-spin' : ''} />
              <span>{syncing ? 'Syncing...' : 'Sync'}</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={fetchUsers}
              className="flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white font-medium shadow-lg shadow-cyan-500/25 transition-all"
            >
              <SafeIcon icon={FiRefreshCw} />
              <span>Refresh</span>
            </motion.button>
          </div>
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto rounded-xl border border-slate-700/50 bg-slate-800/30">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700/50 bg-slate-800/50">
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">User</th>
                <th className="px-4 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Auth</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Role</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Subscription</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Last Login</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/30">
              {filteredUsers.map((user) => {
                const planBadge = getPlanBadge(user);
                // Get user initials from name
                const getInitials = () => {
                  if (user.first_name && user.last_name) {
                    return (user.first_name[0] + user.last_name[0]).toUpperCase();
                  }
                  if (user.name && user.name.trim()) {
                    const parts = user.name.trim().split(/\s+/);
                    if (parts.length >= 2) {
                      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                    }
                    return user.name[0]?.toUpperCase() || '?';
                  }
                  // Fallback to email - get first letter of email username
                  if (user.email) {
                    const username = user.email.split('@')[0];
                    return username[0]?.toUpperCase() || '?';
                  }
                  return '?';
                };
                // Use auth_provider from database (set by sync_new_user trigger)
                const authProvider = user.auth_provider || 'email';
                return (
                  <motion.tr
                    key={user.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-slate-700/20 transition-colors cursor-pointer"
                    onClick={() => openEditPanel(user)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/30 to-cyan-500/30 flex items-center justify-center border border-purple-500/30">
                          <span className="text-white font-semibold text-sm">
                            {getInitials()}
                          </span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.name || 'No name'}</p>
                          <p className="text-slate-400 text-sm">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                        authProvider === 'google'
                          ? 'bg-red-500/20 text-red-400 border-red-500/30'
                          : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                      }`}>
                        {authProvider === 'google' ? (
                          <>
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            Google
                          </>
                        ) : (
                          <>
                            <SafeIcon icon={FiIcons.FiMail} className="text-xs" />
                            Email
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newRole = user.role === 'admin' ? 'user' : 'admin';
                          const effectiveId = user.user_id || user.id;
                          if (window.confirm(`Change ${user.email} role to "${newRole}"?`)) {
                            updateUserRole(effectiveId, newRole);
                          }
                        }}
                        disabled={updatingUser === (user.user_id || user.id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border cursor-pointer hover:opacity-80 transition-opacity disabled:opacity-50 ${
                          user.role === 'admin' 
                            ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' 
                            : 'bg-slate-500/20 text-slate-400 border-slate-500/30'
                        }`}
                        title={`Click to ${user.role === 'admin' ? 'remove admin' : 'make admin'}`}
                      >
                        <SafeIcon icon={user.role === 'admin' ? FiShield : FiUser} className="text-xs" />
                        {updatingUser === (user.user_id || user.id) ? '...' : (user.role === 'admin' ? 'Admin' : 'User')}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${planBadge.color}`}>
                        <SafeIcon icon={planBadge.icon} className="text-xs" />
                        {planBadge.text}
                      </span>
                      {user.analyses_used_this_month > 0 && user.plan?.analyses_limit && (
                        <span className="ml-2 text-xs text-slate-500">
                          {user.analyses_used_this_month}/{user.plan.analyses_limit}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
                        user.is_active 
                          ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                          : 'bg-red-500/20 text-red-400 border-red-500/30'
                      }`}>
                        <SafeIcon icon={user.is_active ? FiToggleRight : FiToggleLeft} className="text-xs" />
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-400 text-sm">
                      {user.last_login 
                        ? new Date(user.last_login).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'Never'
                      }
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => openEditPanel(user)}
                          className="p-2 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-colors"
                          title="Edit user"
                        >
                          <SafeIcon icon={FiEdit} />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => toggleUserStatus(user.user_id)}
                          disabled={updatingUser === user.user_id}
                          className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-colors disabled:opacity-50"
                          title="Toggle status"
                        >
                          <SafeIcon icon={user.is_active ? FiToggleRight : FiToggleLeft} />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setDeleteConfirm(user)}
                          className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                          title="Delete user"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </motion.button>
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
          
          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <SafeIcon icon={FiUsers} className="text-4xl mb-3 mx-auto opacity-50" />
              <p>No users found</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Side Panel for Editing User */}
      <AnimatePresence>
        {sidePanelOpen && selectedUser && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidePanelOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            />
            
            {/* Side Panel */}
            <motion.div
              initial={{ x: '100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-gradient-to-br from-slate-900 to-slate-800 border-l border-slate-700/50 shadow-2xl z-50 overflow-y-auto"
            >
              <div className="p-6">
                {/* Panel Header */}
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-xl font-bold text-white">Edit User</h3>
                  <button
                    onClick={() => setSidePanelOpen(false)}
                    className="p-2 rounded-lg hover:bg-slate-700/50 transition-colors text-slate-400 hover:text-white"
                  >
                    <SafeIcon icon={FiX} className="text-xl" />
                  </button>
                </div>

                {/* User Info */}
                <div className="flex items-center gap-4 mb-8 p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500/30 to-cyan-500/30 flex items-center justify-center border border-purple-500/30">
                    <span className="text-white font-bold text-lg">
                      {(selectedUser.name || selectedUser.email)?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div>
                    <p className="text-white font-semibold">{selectedUser.name || 'No name'}</p>
                    <p className="text-slate-400 text-sm">{selectedUser.email}</p>
                    <p className="text-slate-500 text-xs mt-1">
                      Joined {new Date(selectedUser.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Edit Form */}
                <div className="space-y-6">
                  {/* Role */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Role</label>
                    <div className="relative">
                      <select
                        value={editForm.role}
                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                        className="w-full appearance-none bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 pr-10 text-white focus:outline-none focus:border-purple-500/50 cursor-pointer"
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                      <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Subscription Plan */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Subscription Plan</label>
                    <div className="relative">
                      <select
                        value={editForm.planId}
                        onChange={(e) => setEditForm({ ...editForm, planId: e.target.value })}
                        className="w-full appearance-none bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 pr-10 text-white focus:outline-none focus:border-purple-500/50 cursor-pointer"
                      >
                        <option value="">No Plan</option>
                        {plans.map(plan => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name} {planPriceLabel(plan)}
                          </option>
                        ))}
                      </select>
                      <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                    {editForm.planId && (
                      <div className="mt-3 p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                        {(() => {
                          const selectedPlan = plans.find(p => p.id === editForm.planId);
                          if (!selectedPlan) return null;
                          return (
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-400">Tier:</span>
                                <span className="text-white capitalize">{selectedPlan.tier}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-400">Analyses:</span>
                                <span className="text-white">{selectedPlan.analyses_limit || 'Unlimited'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-400">Price:</span>
                                <span className="text-white">
                                  {selectedPlan.is_custom_pricing
                                    ? 'Contact sales'
                                    : selectedPlan.price_usd_month === 0
                                      ? 'Free'
                                      : `$${selectedPlan.price_usd_month}/mo`}
                                  {selectedPlan.price_usd_year && selectedPlan.price_usd_year > 0 && (
                                    <span className="text-slate-500 ml-2">
                                      · ${selectedPlan.price_usd_year}/yr
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Subscription Status */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Subscription Status</label>
                    <div className="relative">
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        className="w-full appearance-none bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 pr-10 text-white focus:outline-none focus:border-purple-500/50 cursor-pointer"
                      >
                        <option value="active">Active</option>
                        <option value="trialing">Trialing</option>
                        <option value="canceled">Canceled</option>
                      </select>
                      <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Billing Type */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Billing Type</label>
                    <div className="relative">
                      <select
                        value={editForm.billingType}
                        onChange={(e) => setEditForm({ ...editForm, billingType: e.target.value })}
                        className="w-full appearance-none bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 pr-10 text-white focus:outline-none focus:border-purple-500/50 cursor-pointer"
                      >
                        <option value="monthly">Monthly</option>
                        <option value="annual">Annual</option>
                        <option value="lifetime">Lifetime</option>
                      </select>
                      <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                    {editForm.billingType === 'lifetime' && (
                      <p className="mt-2 text-xs text-emerald-400">✓ Lifetime access - never expires</p>
                    )}
                  </div>

                  {/* Billing Status */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Billing Status</label>
                    <div className="relative">
                      <select
                        value={editForm.billingStatus}
                        onChange={(e) => setEditForm({ ...editForm, billingStatus: e.target.value })}
                        className="w-full appearance-none bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 pr-10 text-white focus:outline-none focus:border-purple-500/50 cursor-pointer"
                      >
                        <option value="none">None</option>
                        <option value="active">Active (Paid)</option>
                        <option value="expired">Expired</option>
                        <option value="past_due">Past Due</option>
                      </select>
                      <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Free Trial Toggle — reads the real trial_ends_at on the
                      selected user so the label reflects the actual grant
                      length (e.g. 3mo from admin_grant_reply_trial), not a
                      hardcoded "30 days" that was misleading admins. */}
                  {(() => {
                    const trialEnd = selectedUser?.trial_ends_at
                      ? new Date(selectedUser.trial_ends_at)
                      : null;
                    const trialActive = editForm.isInTrial && trialEnd && trialEnd > new Date();
                    const daysLeft = trialEnd
                      ? Math.max(0, Math.ceil((trialEnd.getTime() - Date.now()) / 86_400_000))
                      : 0;
                    return (
                      <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/30">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-medium text-slate-300">Free Trial</h4>
                            <p className="text-xs text-slate-500 mt-1">
                              {trialActive
                                ? `Pro Trader access · until ${trialEnd.toLocaleDateString()}`
                                : 'Pro Trader access — trial length is set by trial_ends_at'}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setEditForm({ ...editForm, isInTrial: !editForm.isInTrial })}
                            className={`relative w-12 h-6 rounded-full transition-colors ${
                              editForm.isInTrial
                                ? 'bg-gradient-to-r from-purple-500 to-cyan-500'
                                : 'bg-slate-700'
                            }`}
                          >
                            <span
                              className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                                editForm.isInTrial ? 'left-7' : 'left-1'
                              }`}
                            />
                          </button>
                        </div>

                        {/* Trial-status readout — real data, not a static label */}
                        {editForm.isInTrial && trialActive && (
                          <div className="mt-3 flex items-center gap-2 text-xs">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                            <span className="text-emerald-300 font-mono">
                              {daysLeft} days remaining · ends {trialEnd.toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        {editForm.isInTrial && !trialActive && (
                          <p className="mt-3 text-xs text-amber-400">
                            ⚠️ Toggle is ON but no valid trial_ends_at. Saving will default to 30 days —
                            or grant a longer trial via the Growth Feedback panel to write a real end date.
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {/* AI Provider Override */}
                  <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/30">
                    <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                      <SafeIcon icon={FiIcons.FiCpu} className="text-cyan-400" />
                      AI Provider Override
                    </h4>
                    <div className="space-y-3">
                      <div className="relative">
                        <select
                          value={editForm.aiProvider}
                          onChange={(e) => {
                            const newProvider = e.target.value;
                            setEditForm({ 
                              ...editForm, 
                              aiProvider: newProvider,
                              aiModel: newProvider === 'gemini' ? 'google/gemini-3-pro-preview'
                                     : newProvider === 'openai' ? 'gpt-5.5'
                                     : ''
                            });
                          }}
                          className="w-full appearance-none bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 pr-10 text-white focus:outline-none focus:border-purple-500/50 cursor-pointer"
                        >
                          <option value="">Global Default</option>
                          <option value="openai">🤖 OpenAI (ChatGPT)</option>
                          <option value="gemini">💎 Google Gemini</option>
                        </select>
                        <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      </div>
                      {editForm.aiProvider && (
                        <div className="relative">
                          <select
                            value={editForm.aiModel}
                            onChange={(e) => setEditForm({ ...editForm, aiModel: e.target.value })}
                            className="w-full appearance-none bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 pr-10 text-white focus:outline-none focus:border-purple-500/50 cursor-pointer"
                          >
                            {editForm.aiProvider === 'openai' && (
                              <>
                                <option value="gpt-5.5">gpt-5.5</option>
                                <option value="gpt-5.4">gpt-5.4</option>
                                <option value="gpt-5.4-mini">gpt-5.4-mini</option>
                              </>
                            )}
                            {editForm.aiProvider === 'gemini' && (
                              <>
                                <option value="google/gemini-3-pro-preview">Gemini 3 Pro Preview</option>
                                <option value="google/gemini-3-flash-preview">Gemini 3 Flash Preview</option>
                                <option value="google/gemini-2.5-pro">Gemini 2.5 Pro</option>
                                <option value="google/gemini-2.5-flash">Gemini 2.5 Flash</option>
                              </>
                            )}
                          </select>
                          <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                        </div>
                      )}
                      <p className="text-xs text-slate-500">
                        {editForm.aiProvider 
                          ? `This user will use ${editForm.aiProvider === 'gemini' ? 'Google Gemini' : 'OpenAI'} regardless of global setting`
                          : 'Uses the global AI provider set in AI Model Config'}
                      </p>
                    </div>
                  </div>

                  {/* Usage Stats */}
                  <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/30">
                    <h4 className="text-sm font-medium text-slate-300 mb-3">Usage This Month</h4>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">AI Analyses:</span>
                      <span className="text-white font-medium">
                        {selectedUser.analyses_used_this_month || 0}
                        {selectedUser.plan?.analyses_limit && ` / ${selectedUser.plan.analyses_limit}`}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={saveUserChanges}
                  disabled={updatingUser}
                  className="w-full mt-8 flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 disabled:opacity-50 text-white font-medium shadow-lg shadow-purple-500/25 transition-all"
                >
                  {updatingUser ? (
                    <>
                      <SafeIcon icon={FiRefreshCw} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiCheck} />
                      Save Changes
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDeleteConfirm(null)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-gradient-to-br from-slate-900 to-slate-800 border border-red-500/30 rounded-2xl p-6 shadow-2xl z-50"
            >
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                  <SafeIcon icon={FiTrash2} className="text-red-400 text-3xl" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Delete User</h3>
                <p className="text-slate-400 mb-4">
                  Are you sure you want to permanently delete this user?
                </p>
                <div className="bg-slate-800/50 rounded-lg p-3 mb-6">
                  <p className="text-white font-medium">{deleteConfirm.name || 'No name'}</p>
                  <p className="text-slate-400 text-sm">{deleteConfirm.email}</p>
                </div>
                <p className="text-red-400 text-sm mb-6">
                  ⚠️ This action cannot be undone. All user data will be permanently removed.
                </p>
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setDeleteConfirm(null)}
                    disabled={deleting}
                    className="flex-1 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 text-white font-medium transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => deleteUser(deleteConfirm.user_id || deleteConfirm.id)}
                    disabled={deleting}
                    className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {deleting ? (
                      <>
                        <SafeIcon icon={FiRefreshCw} className="animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiTrash2} />
                        Delete
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserManagementPanel;
